import { gql } from "@apollo/client";
import { alpha, ButtonBase } from "@mui/material";
import ClipSvg from "assets/reactions/clip.svg";
import Avatar from "atoms/Avatar";
import HoverModal from "atoms/HoverModal";
import StackedAvatars from "atoms/StackedAvatars";
import WithTooltip from "atoms/WithTooltip";
import { COLOR_GREY, COLOR_SECONDARY } from "helpers/colors";
import { EVENT_DEFAULT_FINISH_OFFSET_SECONDS, EVENT_DEFAULT_START_OFFSET_SECONDS } from "helpers/constants";
import { getEventTemplate } from "helpers/EVENT_TEMPLATES";
import { gaEvent } from "helpers/gtag";
import {
  SHORTCUT_KEY_CODE_VIDEO_PAGE_NEW_CLIP,
  SHORTCUT_KEY_CODE_VIDEO_PAGE_NEW_STAT,
  SHORTCUT_KEY_LABEL_VIDEO_PAGE_NEW_CLIP,
  SHORTCUT_KEY_LABEL_VIDEO_PAGE_NEW_STAT,
} from "helpers/SHORTCUTS";
import useActionFragment from "helpers/useActionFragment";
import useDOMEvent from "helpers/useDOMEvent";
import { Plus } from "mdi-material-ui";
import FormDialog from "molecules/FormDialog";
import { videoPageContext } from "pages/VideoPage";
import React, { useContext, useState } from "react";

import HighlightEditor, { HighlightEditorFragment } from "./HighlightEditor";

export const VideoNewHighlightButtonsFragment = gql`
  fragment VideoNewHighlightButtonsFragment on Game {
    id
    canOnsenTagVideo
    league {
      id
      sport
    }
    durationSeconds
    home {
      id
    }
    currentUserDefaultGamePlayer {
      id
      team {
        id
      }
      person {
        id
      }
    }
    ...HighlightEditorFragment
  }
  ${HighlightEditorFragment}
`;

export default function VideoNewHighlightButtons({ game }) {
  const onsenUpsertEvent = useActionFragment("onsenUpsertEvent", "game { id events { id } }");
  const [newHighlightOpen, newHighlightOpenSet] = useState(false);
  const { currentPeriodNumber, currentTime, isVideoPageModalActive } = useContext(videoPageContext);

  // new highlight
  const [event, eventSet] = useState({});

  const eventTemplate = getEventTemplate(game?.league.sport, event);
  const eventValid = !!eventTemplate;

  const openNewHighlight = () => {
    eventSet({
      occurredAtSeconds: currentTime,
      startAtSeconds: Math.max(currentTime - EVENT_DEFAULT_START_OFFSET_SECONDS, 0),
      finishAtSeconds: Math.min(currentTime + EVENT_DEFAULT_FINISH_OFFSET_SECONDS, game.durationSeconds),
      team: game.currentUserDefaultGamePlayer?.team || game.home,
      person: game.currentUserDefaultGamePlayer?.person,
      event: {},
    });
    newHighlightOpenSet(true);
    gaEvent("event_create_open");
  };

  const createReaction = async () => {
    await onsenUpsertEvent({
      input: {
        gameId: game.id,
        type: "Reaction",
        occurredAtSeconds: currentTime,
        period: currentPeriodNumber,
        reactionType: "clip",
      },
    });
    gaEvent("event_create_reaction", {
      reaction_type: "clip",
    });
  };

  useDOMEvent("keydown", (event) => {
    if (!isVideoPageModalActive) return;
    if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
    if (event.code === SHORTCUT_KEY_CODE_VIDEO_PAGE_NEW_STAT) openNewHighlight();
    if (event.code === SHORTCUT_KEY_CODE_VIDEO_PAGE_NEW_CLIP) createReaction();
  });

  if (!game?.canOnsenTagVideo) {
    return null;
  }

  return (
    <>
      {game?.statsVisible && (
        <HoverModal
          element={
            <StackedAvatars
              size={48}
              primaryAvatar={
                <Avatar
                  element={
                    <WithTooltip
                      tooltip="Add stat"
                      shortcutLabel={SHORTCUT_KEY_LABEL_VIDEO_PAGE_NEW_STAT}
                      inverse
                      placement="top"
                      element={
                        <ButtonBase
                          focusRipple
                          accessKey="d"
                          disabled={!game}
                          data-video-joyride="tag-stat-highlights"
                          onClick={() => {
                            openNewHighlight();
                          }}
                        />
                      }
                    />
                  }
                  color={COLOR_SECONDARY}
                  text="STAT"
                />
              }
              secondaryAvatar={<Avatar color={alpha(COLOR_GREY, 0.8)} text={<Plus fontSize="inherit" />} />}
            />
          }
        />
      )}
      <HoverModal
        element={
          <StackedAvatars
            size={48}
            primaryAvatar={
              <Avatar
                element={
                  <WithTooltip
                    tooltip="Create a clip"
                    shortcutLabel={SHORTCUT_KEY_LABEL_VIDEO_PAGE_NEW_CLIP}
                    accessKey="c"
                    inverse
                    placement="top"
                    element={
                      <ButtonBase data-video-joyride="tag-reaction-highlights" focusRipple onClick={createReaction} />
                    }
                  />
                }
                imgSrc={ClipSvg}
              />
            }
            secondaryAvatar={<Avatar color={alpha(COLOR_GREY, 0.8)} text={<Plus fontSize="inherit" />} />}
          />
        }
      />
      <FormDialog
        inverse
        title="Add Highlight"
        open={newHighlightOpen}
        onClose={() => newHighlightOpenSet(false)}
        onSubmit={
          eventValid &&
          (async () => {
            await onsenUpsertEvent({
              input: {
                gameId: game.id,
                period: currentPeriodNumber,
                personId: event.person?.id,
                teamId: event.team?.id,
                occurredAtSeconds: event.occurredAtSeconds,
                startAtSeconds: event.startAtSeconds,
                finishAtSeconds: event.finishAtSeconds,
                x: event.coordinates?.x,
                y: event.coordinates?.y,
                ...eventTemplate.params,
              },
            });
            gaEvent("event_create_save", {
              event_type: eventTemplate.params.type,
            });
          })
        }
        fullWidth
        fullHeight
        submitButtonLabel={`Add ${eventTemplate?.title}`}
      >
        <HighlightEditor open={newHighlightOpen} game={game} event={event} eventSet={eventSet} />
      </FormDialog>
    </>
  );
}
